import { CalendarIcon } from '@/assets/icons';
import { REGEX } from '@/constant';
import { useEditableDetail } from '@/hooks';
import { cn, dayjs, formatDate, SUPPORT_FORMAT_DATE_LIST } from '@/utils';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { DatePicker as DatePickerAntd } from 'antd';

import type { DatePickerProps } from 'antd';
import type { Dayjs } from 'dayjs';
import { isNilOrEmpty } from 'ramda-adjunct';
import type { BaseInfo, PickerRef } from 'rc-picker/lib/interface';

export interface IDatePickerProps extends DatePickerProps {
	label?: string;
	inputClassName?: string;
	containerClassName?: string;
	labelClassName?: string;
	horizontal?: boolean;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	format?: string;
	formatToUpdate?: string;
	isDetail?: boolean;
	startTimeKey?: string;
	endTimeKey?: string;
	textEditClassName?: string;
	unit?: string;
}

const DatePicker: React.FC<IDatePickerProps> = ({
	label,
	placeholder = 'YYYY-MM-DD',
	inputClassName,
	containerClassName,
	horizontal = true,
	labelClassName,
	required,
	touched,
	errorCode,
	hasServerError,
	format,
	isDetail = false,
	textEditClassName,
	unit,
	...rest
}) => {
	const { isUpdate, editable, handleLabelClick } = useEditableDetail({
		fieldName: rest.name as string,
	});

	const DATE_FORMAT = format || ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY.MM.DD'];

	const [open, setOpen] = useState(false);

	let value: Dayjs | null | undefined = useMemo(() => {
		if (isNilOrEmpty(rest.value)) {
			return null;
		}
		if (typeof rest.value === 'string') {
			return dayjs(rest.value, SUPPORT_FORMAT_DATE_LIST);
		}
		return rest.value || null;
	}, [rest.value]);

	const datePickerRef = useRef<PickerRef>(null);

	useEffect(() => {
		const inputElement = datePickerRef.current?.nativeElement;

		const handleInput = (event: Event) => {
			const target = event.target as HTMLInputElement;
			const value = target.value;
			const cleanValue = value.replace(/[.,]/g, '');
			if (cleanValue !== value) {
				target.value = cleanValue;
			}
		};

		if (inputElement) {
			inputElement.addEventListener('keydown', handleKeyDown);
			inputElement.addEventListener('input', handleInput);
		}
		return () => {
			if (inputElement) {
				inputElement.removeEventListener('keydown', handleKeyDown);
				inputElement.removeEventListener('input', handleInput);
			}
		};
	}, [datePickerRef?.current]);

	const handleKeyDown = (event: any) => {
		const key = event.key;

		let allowedControlKeys = rest?.showTime
			? REGEX.DATE_TIME_INPUT_ALLOWED_KEYS_REGEX
			: REGEX.DATE_INPUT_ALLOWED_KEYS_REGEX;

		if (!allowedControlKeys.test(key)) {
			event.preventDefault();
		}

		if (key === 'Enter') {
			datePickerRef?.current?.blur();
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLElement>, info: BaseInfo) => {
		const input = e.target as HTMLInputElement;
		input.setAttribute('inputmode', 'numeric');
		input.setAttribute('pattern', '[0-9]*');
	};

	useEffect(() => {
		if (editable && isUpdate && isDetail) {
			datePickerRef?.current?.focus();
			setOpen(true);
		}
	}, [editable]);

	return (
		<div
			className={cn(
				'w-full flex gap-2',
				horizontal ? 'items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2 min-w-[110px]',
						labelClassName
					)}
				>
					{label} {required && <span className="text-red6">*</span>}
				</label>
			)}
			{isDetail && !editable ? (
				<div
					className={cn(
						'flex items-center box-border w-full text-[13px] h-[30px] text-font2 py-[5px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text',
						rest.disabled &&
							'cursor-not-allowed hover:border-none hover:bg-porcelain',
						textEditClassName
					)}
					onClick={!rest.disabled ? handleLabelClick : undefined}
				>
					{formatDate(value, { outputFormat: format || 'YYYY-MM-DD' }) || '-'}
					{unit && <p className="ps-1 text-[13px] my-auto w-[100px]">{unit}</p>}
				</div>
			) : (
				<div className={cn('flex-1', inputClassName)}>
					<DatePickerAntd
						suffixIcon={<CalendarIcon />}
						size="large"
						status={
							(touched && errorCode) || hasServerError ? 'error' : undefined
						}
						{...rest}
						value={value}
						style={{ width: '100%' }}
						placeholder={placeholder}
						format={DATE_FORMAT}
						className={cn(
							'xl:rounded-none h-[30px] text-[13px]',
							inputClassName
						)}
						ref={datePickerRef}
						open={open}
						onOpenChange={setOpen}
						onBlur={() => {
							window.scrollTo(0, 0);
						}}
						onFocus={handleFocus}
					/>
				</div>
			)}
		</div>
	);
};

export default memo(DatePicker);
